import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, put, post, postFiles, patch } from "../../config/axios";
import { Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";
import { useDebouncedValue } from "../../helper/debounce";
import { toastMessage } from "../../utils/toastMessage";
import FormModal from "../../components/Custom/FormModal/FormModal";
import AppointmentStatusCounts from "./AppointmentStatusCount";

// Define columns and form fields for specialities
const inpersonappointmentTableColumns = [
    { id: "S.No", label: "S.No", minWidth: 70, align: "left" },
    { id: "doctorId.fullname", label: "Doctor Name", minWidth: 150, align: "left" },
    { id: "patientId.fullname", label: "Patient Name", minWidth: 150, align: "left" },
    { id: "patientId.phone", label: "Patient Phone", minWidth: 100, align: "left" },
    { id: "specialityId.speciality", label: "Speciality", minWidth: 120, align: "left" },
    { id: "createdAt", label: "Booked On", minWidth: 150, align: "center" },
    { id: "date", label: "Appointment Date", minWidth: 120, align: "center" },
    { id: "startTime", label: "Start Time", minWidth: 100, align: "center" },
    { id: "endTime", label: "End Time", minWidth: 100, align: "center" },
    { id: "doctorId.consultationFee", label: "Consultation Fee", minWidth: 100, align: "center" },
    { id: "doctorId.processingFee", label: "Processing Fee", minWidth: 100, align: "center" },
    { id: "status", label: "Status", minWidth: 100, align: "center" },
    { id: "action", label: "Actions", align: "center" },

];



const appointmentFormFields = [
    {
        name: "status",
        label: "Status",
        type: "option",
        options: ["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED","NO_SHOW"],
        required: true,
      },
];

const InPersonAppointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteSpeciality, setDeleteSpeciality] = useState("");
    const [search, setSearch] = useState("");
    const [message, setMessage] = useState("");
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalData, setTotalData] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const debouncedSearch = useDebouncedValue(search, 500);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [statusCounts, setStatusCounts] = useState({
        PENDING: 0,
        CONFIRMED: 0,
        COMPLETED: 0,
        CANCELLED: 0
    });
    const [selectedStatus, setSelectedStatus] = useState('');
    const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });


    const fetchSpecialities = async (searchValue, status = selectedStatus, startDate = "", endDate = "") => {
        setLoading(true);
        try {
            const response = await get(
                `/api/dashboard/inPersonFeatures/get-all-doctor-appointments?page=${page}&limit=${10}&search=${searchValue}&status=${status}&startDate=${startDate}&endDate=${endDate}`
            );

            setAppointments(
                response?.appointments.map((item) => ({
                    ...item,
                    action: { edit: true },
                }))
            );
            setLoading(false);
            setStatusCounts(response?.statusCounts);
            setPageCount(response?.totalPage);
            setTotalData(response?.totalData);
            setPerPage(response?.perPage);
            setCurrentPage(response?.currentPage);
        } catch (error) {
            console.log("Error fetching specialities", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (search === "") {
            fetchSpecialities("");
        } else if (debouncedSearch) {
            fetchSpecialities(debouncedSearch);
        }
    }, [search, debouncedSearch, message, page]);

    const handleEdit = (row) => {
        setEditData({
            ...row,
            status: row.status, // Default to PENDING if status is missing
        });
        setEditModal(true);
    };
    

    const handleDelete = (row) => {
        openModal("delete", row);
    };

    const handleDeleteSpeciality = async (row) => {
        try {
            const deletedSpeciality = await patch(
                `/api/dashboard/inPersonFeatures/delete-speciality/${row._id}`
            );
            setMessage(deletedSpeciality?.message);
            toastMessage(deletedSpeciality?.message, "success");
            setDeleteModalOpen(false);
        } catch (error) {
            console.error(error);
            setMessage("Something went wrong!");
            toastMessage("Something went wrong!", "error");
        }
    };

    const handleActive = async (row, active) => {
        console.log("row==", row);
        const { _id: id } = row;

        setLoading(true);
        try {
            let response = await put(
                `/api/dashboard/inPersonFeatures/update-speciality/${row}`,
                {
                    active: active,
                }
            );

            const specialityTitle = response?.data?.speciality;
            const message = active
                ? `${specialityTitle} successfully activated.`
                : `${specialityTitle} successfully deactivated.`;
            setLoading(false);
            setMessage(message);
            toastMessage(message, "success");
        } catch (error) {
            setLoading(false);
            toastMessage("Error updating status", "error");
        }
    };

    const handleSearch = (searchText) => {
        setSearch(searchText.trim());
        setPage(1);
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
    };

    const handleChange = (page) => {
        setPage(page);
    };

    const openModal = (type, dataForEdit) => {
        if (type === "add") {
            setIsModalOpen(true);
            setEditData({});
        } else if (type === "edit") {
            setEditModal(true);
            setEditData(dataForEdit);
        } else if (type === "delete") {
            setDeleteModalOpen(true);
            setDeleteSpeciality(dataForEdit);
        }
    };

    const closeModal = (type) => {
        if (type === "add") {
            setIsModalOpen(false);
        } else if (type === "edit") {
            setEditModal(false);
            setEditData({});
        }
    };

    const handleSubmit = async (formData, isEditing, id) => {
        setLoading(true);
        try {
            if (isEditing) {
                const response = await put(
                    "/api/dashboard/inPersonFeatures/update-appointment-status",
                    {
                        appointmentId: id,
                        status: formData.status,
                    }
                );
    
                console.log("Response:", response.data);
                setMessage(response.message);
                toastMessage(response.message, "success");
            }
    
            setIsModalOpen(false);
            setEditModal(false);
            setEditData({});
            fetchSpecialities(search, selectedStatus, dateFilter.startDate, dateFilter.endDate);
            
        } catch (err) {
            console.error("Error updating status:", err);
            setMessage("Error while updating appointment status");
            toastMessage("Error while updating", "error");
        }
        setLoading(false);
    };
    
    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        setPage(1);
        fetchSpecialities(search, status); // Ensure new status is applied
    };
    const handleDateFilterChange = ({ startDate, endDate }) => {
        setDateFilter({ startDate, endDate });
        setPage(1);
        fetchSpecialities(search, selectedStatus, startDate, endDate);
    };

    return (
        <>
            <Layout>
                <div style={{ padding: "1rem" }}>
                    <Typography variant="h5">In-Person Appointments</Typography>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <div style={{ width: "40%" }}>
                            <Searchbar
                                search={handleSearch}
                                placeholder={"Search by name, phone or speciality"}
                                debounceTime={1000}
                            />
                        </div>

                        {/* <Button
                            onClick={() => openModal("add")}
                            variant="outlined"
                            startIcon={<AddIcon fontSize="large" />}
                            style={{ fontWeight: "bold" }}
                        >
                            add speciality
                        </Button> */}
                    </div>
                    <AppointmentStatusCounts
                        counts={statusCounts}
                        selectedStatus={selectedStatus}
                        onStatusChange={handleStatusChange}
                        onFilterChange={handleDateFilterChange}
                    />
                    <CustomTable
                        data={appointments}
                        columns={inpersonappointmentTableColumns}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleActive={(row, active) => handleActive(row, active)}
                        handlePageChange={(page) => handleChange(page)}
                        pageNumber={page}
                        pageCount={pageCount}
                        totalData={totalData}
                        perPage={perPage}
                        currentPage={currentPage}
                        loading={loading}
                    />
                    {search && appointments.length === 0 && !loading && (
                        <Typography variant="body1" sx={{ color: 'red', textAlign: 'center', mt: 2 }}>
                            No appointments found for your search.
                        </Typography>
                    )}
                </div>
            </Layout>
            <DeleteModal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onDelete={handleDeleteSpeciality}
                data={deleteSpeciality}
            />
            <FormModal
                accept="image/*"
                isOpen={isModalOpen || editModal}
                onClose={() => closeModal(editModal ? "edit" : "add")}
                onSubmit={handleSubmit}
                fields={appointmentFormFields}
                header={editModal ? "Change Appointment Status" : "Add Speciality"}
                initialData={editData}
                isEditing={editModal}
            />
        </>
    );
};

export default InPersonAppointments;